import * as React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TaxCategorySelect } from '@/components/ui/tax-category-select';

/**
 * Product information form component
 * Handles basic product details using React Hook Form with Controller
 */
export const ProductInformationForm = () => {
  const { t } = useTranslation(['products', 'common']);
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('productInformation')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <div className="space-y-2">
              <Label htmlFor="name">{t('name')} *</Label>
              <Input id="name" {...field} value={field.value || ''} />
              {errors.name && (
                <p className="mt-1 text-xs leading-tight text-red-600">{errors.name.message as string}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <div className="space-y-2">
              <Label htmlFor="description">{t('description')}</Label>
              <Textarea id="description" {...field} value={field.value || ''} />
              {errors.description && (
                <p className="mt-1 text-xs leading-tight text-red-600">
                  {errors.description.message as string}
                </p>
              )}
            </div>
          )}
        />

        <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
          <Controller
            name="accountingCode"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                <Label htmlFor="accountingCode">
                  {t('accountingCode')}
                </Label>
                <Input id="accountingCode" {...field} value={field.value || ''} />
                {errors.accountingCode && (
                  <p className="mt-1 text-xs leading-tight text-red-600">
                    {errors.accountingCode.message as string}
                  </p>
                )}
              </div>
            )}
          />

          <Controller
            name="mappedTaxCategoryId"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                <Label htmlFor="mappedTaxCategoryId">{t('taxCategory')}</Label>
                <TaxCategorySelect
                  value={field.value}
                  onChange={field.onChange}
                />
                {errors.mappedTaxCategoryId && (
                  <p className="mt-1 text-xs leading-tight text-red-600">
                    {errors.mappedTaxCategoryId.message as string}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        <Controller
          name="unitLabel"
          control={control}
          render={({ field }) => (
            <div className="space-y-2">
              <Label htmlFor="unitLabel">{t('unitLabel')}</Label>
              <Input id="unitLabel" {...field} value={field.value || ''} />
              {errors.unitLabel && (
                <p className="mt-1 text-xs leading-tight text-red-600">
                  {errors.unitLabel.message as string}
                </p>
              )}
            </div>
          )}
        />
      </CardContent>
    </Card>
  );
};
